import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, Text, tokens } from '@hotjar/hotsauce';
import EmptyStateEngagedImage from '@hotjar/shared-assets/images/recordings/overview/empty-state-engaged.png';
import EmptyStateFrustratedImage from '@hotjar/shared-assets/images/recordings/overview/empty-state-frustrated.png';
import EmptyStateRageClickImage from '@hotjar/shared-assets/images/recordings/overview/empty-state-rage-clicks.png';
import EmptyStateUturnsImage from '@hotjar/shared-assets/images/recordings/overview/empty-state-uturns.png';
import EmptyStateErrors from '@hotjar/shared-assets/images/recordings/overview/empty-state-errors.png';
import { RecordingPlaybackTracker } from '@hotjar/webapp/recordings/recording-playback-tracker';
import { increment as statefulIncrementTracker } from '@hotjar/webapp/recordings/useStatefulPlaybackTracker';
import { useTargetingSettings } from '@hotjar/webapp/hooks/useTargetingSettings';
import { useCurrentUser } from '@hotjar/user';
import { useErrorOccurredWidgetVisible } from './hooks/useErrorOccurredWidgetVisible';
import { RecordingsOverviewWidget } from './RecordingsOverviewWidget';
import {
  OverviewTrackingElements,
  OVERVIEW_MOST_RELEVANT_RECORDINGS_TITLE,
  WIDGETS_FETCH_DATA,
} from '../RecordingsPage/utils';
import {
  trackRecordingsOverviewViewed,
  trackOnOverviewPlayClick,
  trackOnOverviewViewAllClick,
} from '../RecordingsPage/utils';

import type { Store } from '../../store/list/types';
import type { Site } from '@hotjar/site';
import {
  RecordingOverviewWidgetCellRageClicks as CellRageClicks,
  RecordingOverviewWidgetCellUturns as CellUturns,
  RecordingOverviewWidgetCellErrors as CellErrors,
  RecordingOverviewWidgetCellFrustrationScore as CellFrustrationScore,
  RecordingOverviewWidgetCellEngagementScore as CellEngagementScore,
} from './RecordingsOverviewCardList';
import { RecordingTile } from '../../components/common/RecordingTile/RecordingTile';
import { Recording } from '@hotjar/webapp/domains/recordings/types';
import { RecordingsOverviewFeedbackLink } from './RecordingsOverviewFeedbackLink';
import { onRecordingsClick } from './utils/onRecordingsClick';
import {
  StyledRecordingsOverviewGrid,
  StyledRecordingTiles,
  StyledRelevantRecordingsHeader,
  StyledRelevantRecordingsHeaderButton,
  StyledRelevantRecordingsHeaderTitleWrapper,
  StyledRelevantRecordingsWrapper,
  StyledSegmentsHeader,
  StyledViewAllRelevantRecordingsTile,
  StyledNotCapturingErrors,
} from './styles';
import { useGetNotWatchedRecordings } from './hooks/useGetNotWatchedRecordings';
import { navigateToViewAllRecordings } from './utils/navigateToViewAllRecordings';
import { RecommendedWidgetFetchConfig } from '../RecordingsPage/utils/getOverviewRecordings';
import { ErrorWidgetUpsell } from './Upsell/ErrorWidgetUpsell';

export type RecommendedWidgetConfig = {
  color: string;
  emptyStateImage?: string;
} & RecommendedWidgetFetchConfig;

export const WIDGETS: { [key: string]: RecommendedWidgetConfig } = {
  RELEVANT: {
    color: `${tokens.color.bg_active}`,
    ...WIDGETS_FETCH_DATA.RELEVANT,
  },
  ENGAGED: {
    emptyStateImage: EmptyStateEngagedImage,
    color: `${tokens.color.bg_success}`,
    ...WIDGETS_FETCH_DATA.ENGAGED,
  },
  RAGE_CLICKS: {
    emptyStateImage: EmptyStateRageClickImage,
    color: `${tokens.color.bg_danger}`,
    ...WIDGETS_FETCH_DATA.RAGE_CLICKS,
  },
  UTURNS: {
    emptyStateImage: EmptyStateUturnsImage,
    color: `${tokens.BRAND_COLORS.noon_100}`,
    ...WIDGETS_FETCH_DATA.UTURNS,
  },
  ERRORS: {
    emptyStateImage: EmptyStateErrors,
    color: `${tokens.BRAND_COLORS.peach_100}`,
    ...WIDGETS_FETCH_DATA.ERRORS,
  },
  FRUSTRATED: {
    emptyStateImage: EmptyStateFrustratedImage,
    color: `${tokens.BRAND_COLORS.magenta_100}`,
    ...WIDGETS_FETCH_DATA.FRUSTRATED,
  },
};

const MOST_RELEVANT_RECORDINGS_OFFSET = 3;

export const RecordingsOverview = ({ store }: { store: Store }): JSX.Element | null => {
  const { data: user } = useCurrentUser();
  const { t } = useTranslation();
  const [hasEnabledCapture, setHasEnabledCapture] = useState(false);
  const {
    widgetsLoaded,
    loaded: recordingsPageLoaded,
    overviewData: {
      recordingsRelevant,
      recordingsWithRageClicks,
      recordingsEngaged,
      recordingsWithErrors,
      recordingsWithUturns,
      recordingsFrustrated,
      recordingsRelevantTotalHits,
      recordingsEngagedTotalHits,
      recordingsWithRageClicksTotalHits,
      recordingsWithErrorsTotalHits,
      recordingsWithUturnsTotalHits,
      recordingsFrustratedTotalHits,
    },
    site,
  } = store.getState();
  const [recordingsRequestTimeRange] = useState(() => {
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const daysAgoTimestamp = currentTimestamp - 1 * 24 * 60 * 60;
    return [daysAgoTimestamp, currentTimestamp];
  });

  useEffect(() => {
    const recordingsCount = {
      recordings_relevant_count: recordingsRelevantTotalHits,
      recordings_with_rage_clicks_count: recordingsWithRageClicksTotalHits,
      recordings_engaged_count: recordingsEngagedTotalHits,
      recordings_with_uturns_count: recordingsWithUturnsTotalHits,
      recordings_with_errors_count: recordingsWithErrorsTotalHits,
      recordings_frustrated_count: recordingsFrustratedTotalHits,
    };
    site && user && widgetsLoaded && trackRecordingsOverviewViewed(site, user.id, recordingsCount);
  }, [
    site,
    user,
    widgetsLoaded,
    recordingsRelevantTotalHits,
    recordingsEngagedTotalHits,
    recordingsWithRageClicksTotalHits,
    recordingsWithErrorsTotalHits,
    recordingsWithUturnsTotalHits,
    recordingsFrustratedTotalHits,
  ]);

  const mostRelevantRecordings = useGetNotWatchedRecordings(
    recordingsRelevant,
    MOST_RELEVANT_RECORDINGS_OFFSET,
  );

  const { sessionCaptureConsoleConsent } = useTargetingSettings(site as Site);
  const { isErrorCapturingFFEnabled, planAllowsErrorCapturing } = useErrorOccurredWidgetVisible(
    site as Site,
  );

  const onPlayClick = useCallback(
    (widgetKey: string, renderPosition: number, elementName: string) => {
      if (site && user) {
        trackOnOverviewPlayClick(site, user.id, {
          title: widgetKey,
          renderPosition,
          elementName,
        });
        RecordingPlaybackTracker.incrementWatched();

        statefulIncrementTracker((site as Site).id, user.id);
      }
    },
    [site, user],
  );

  const trackOnOverviewViewAllClickCallback = useCallback(
    (widgetTitle: string, elementName: string): Promise<void> => {
      if (site && user) {
        return trackOnOverviewViewAllClick(site, user.id, widgetTitle, elementName);
      }

      return Promise.resolve();
    },
    [site, user],
  );

  const onTileClick = useCallback(
    (renderPosition, recording: Recording) => {
      onPlayClick(
        WIDGETS_FETCH_DATA.RELEVANT.widgetKey,
        renderPosition,
        OverviewTrackingElements.MOST_RELEVANT_TILE,
      );

      onRecordingsClick(
        recording,
        recordingsRelevant,
        WIDGETS_FETCH_DATA.RELEVANT.filters,
        WIDGETS_FETCH_DATA.RELEVANT.sortBy,
        recordingsRequestTimeRange,
      );
    },
    [onPlayClick, recordingsRelevant, recordingsRequestTimeRange],
  );

  const onViewMostRelevantRecordingsClick = useCallback(
    async (elementName: string) => {
      if (!site) {
        return;
      }

      await trackOnOverviewViewAllClickCallback(
        OVERVIEW_MOST_RELEVANT_RECORDINGS_TITLE,
        elementName,
      );

      navigateToViewAllRecordings(site.id, WIDGETS.RELEVANT);
    },
    [site, trackOnOverviewViewAllClickCallback],
  );

  if (!recordingsPageLoaded) {
    return null;
  }

  const errorOverview = planAllowsErrorCapturing ? (
    <RecordingsOverviewWidget
      recordings={recordingsWithErrors}
      count={recordingsWithErrorsTotalHits}
      widgetConfig={WIDGETS.ERRORS}
      onPlayClick={(...args) => onPlayClick(...args)}
      trackOnViewAll={(...args) => trackOnOverviewViewAllClickCallback(...args)}
      overriddenCardState={
        !sessionCaptureConsoleConsent && !hasEnabledCapture ? (
          <StyledNotCapturingErrors
            siteId={site?.id}
            onEnableCapture={() => setHasEnabledCapture(true)}
          />
        ) : null
      }
    >
      <CellErrors />
    </RecordingsOverviewWidget>
  ) : (
    <ErrorWidgetUpsell site={site as Site} />
  );

  return (
    <Loader size="xl" isLoading={!widgetsLoaded}>
      {mostRelevantRecordings && (
        <StyledRelevantRecordingsWrapper>
          <StyledRelevantRecordingsHeader>
            <StyledRelevantRecordingsHeaderTitleWrapper>
              <Text textType="header5" as="h2">
                {t('common:recordings.recordings-overview.most-relevant.title', {
                  count: mostRelevantRecordings.length,
                })}
              </Text>
              <StyledRelevantRecordingsHeaderButton
                onClick={() =>
                  onViewMostRelevantRecordingsClick(OverviewTrackingElements.MOST_RELEVANT_BUTTON)
                }
                kind="ghost_standout"
                size="s"
              >{`${t(
                'common:recordings.recordings-overview.view-all',
              )}`}</StyledRelevantRecordingsHeaderButton>
            </StyledRelevantRecordingsHeaderTitleWrapper>
            <RecordingsOverviewFeedbackLink />
          </StyledRelevantRecordingsHeader>
          <Text textType="caption" textColor="text_light">
            {t('common:recordings.recordings-overview.most-relevant.subtitle')}
          </Text>
          <StyledRecordingTiles>
            {mostRelevantRecordings.map((recording, i) => (
              <RecordingTile
                recording={recording}
                key={`recording-tile-${recording.id}`}
                onClick={() => onTileClick(i + 1, recording)}
              />
            ))}
            <StyledViewAllRelevantRecordingsTile
              onClick={() =>
                onViewMostRelevantRecordingsClick(OverviewTrackingElements.MOST_RELEVANT_TILE)
              }
            >
              {t('common:recordings.recordings-overview.view-recordings', {
                count: recordingsRelevantTotalHits,
              })}
            </StyledViewAllRelevantRecordingsTile>
          </StyledRecordingTiles>
        </StyledRelevantRecordingsWrapper>
      )}

      <StyledSegmentsHeader textType="header5" as="h2">
        {t('common:recordings.recordings-overview.recommended-segments.title')}
      </StyledSegmentsHeader>
      <StyledRecordingsOverviewGrid>
        <RecordingsOverviewWidget
          recordings={recordingsFrustrated}
          count={recordingsFrustratedTotalHits}
          widgetConfig={WIDGETS.FRUSTRATED}
          onPlayClick={(...args) => onPlayClick(...args)}
          trackOnViewAll={(...args) => trackOnOverviewViewAllClickCallback(...args)}
        >
          <CellFrustrationScore />
        </RecordingsOverviewWidget>

        <RecordingsOverviewWidget
          recordings={recordingsEngaged}
          count={recordingsEngagedTotalHits}
          widgetConfig={WIDGETS.ENGAGED}
          onPlayClick={(...args) => onPlayClick(...args)}
          trackOnViewAll={(...args) => trackOnOverviewViewAllClickCallback(...args)}
        >
          <CellEngagementScore />
        </RecordingsOverviewWidget>

        {isErrorCapturingFFEnabled && errorOverview}

        <RecordingsOverviewWidget
          recordings={recordingsWithRageClicks}
          count={recordingsWithRageClicksTotalHits}
          widgetConfig={WIDGETS.RAGE_CLICKS}
          onPlayClick={(...args) => onPlayClick(...args)}
          trackOnViewAll={(...args) => trackOnOverviewViewAllClickCallback(...args)}
        >
          <CellRageClicks />
        </RecordingsOverviewWidget>
        <RecordingsOverviewWidget
          recordings={recordingsWithUturns}
          count={recordingsWithUturnsTotalHits}
          widgetConfig={WIDGETS.UTURNS}
          onPlayClick={(...args) => onPlayClick(...args)}
          trackOnViewAll={(...args) => trackOnOverviewViewAllClickCallback(...args)}
        >
          <CellUturns />
        </RecordingsOverviewWidget>
      </StyledRecordingsOverviewGrid>
    </Loader>
  );
};
